import { useEffect, useRef } from 'react';

/**
 * useAutoRefresh
 * Re-invokes `fetchFn` automatically so pages stay in sync with the backend
 * without the user having to press the browser refresh button.
 *
 * Triggers:
 *  - Every `intervalMs` milliseconds (default 30s)
 *  - When the tab regains focus (window focus)
 *  - When the tab becomes visible again (visibilitychange)
 *
 * Skips the refresh (so it does NOT disturb the user mid-task) when:
 *  - The tab is hidden
 *  - An input / textarea / select / contenteditable element has focus
 *    (i.e. the user is typing or actively picking something)
 *  - A modal / dialog / popover / dropdown / sheet is currently open
 *    (detected via the `[role="dialog"]` / `[data-state="open"]` markers
 *     emitted by Radix-based shadcn components)
 *  - The user has selected text on the page
 *
 * @param {Function} fetchFn - callback that refetches the page's data
 * @param {Object}   options
 * @param {number}   options.intervalMs - polling interval, default 30000 ms
 * @param {boolean}  options.enabled    - disable auto-refresh when false
 */
const isUserBusy = () => {
  if (typeof document === 'undefined') return true;
  if (document.visibilityState !== 'visible') return true;

  // 1. Active text input / select / contenteditable
  const ae = document.activeElement;
  if (ae) {
    const tag = (ae.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
    if (ae.isContentEditable) return true;
  }

  // 2. Open dialog / popover / dropdown / sheet (Radix uses these markers)
  const openOverlay = document.querySelector(
    '[role="dialog"][data-state="open"], ' +
    '[role="alertdialog"][data-state="open"], ' +
    '[role="menu"][data-state="open"], ' +
    '[role="listbox"][data-state="open"], ' +
    '[data-radix-popper-content-wrapper]'
  );
  if (openOverlay) return true;

  // 3. The user is selecting text on the page
  const sel = window.getSelection && window.getSelection();
  if (sel && sel.toString && sel.toString().length > 0) return true;

  return false;
};

export default function useAutoRefresh(fetchFn, { intervalMs = 30000, enabled = true } = {}) {
  const fnRef = useRef(fetchFn);

  useEffect(() => {
    fnRef.current = fetchFn;
  }, [fetchFn]);

  useEffect(() => {
    if (!enabled) return undefined;

    const tick = () => {
      if (isUserBusy()) return;
      try {
        fnRef.current && fnRef.current();
      } catch (_) {
        /* swallow refresh errors — never disturb the UI */
      }
    };

    const id = setInterval(tick, intervalMs);

    const onFocus = () => tick();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') tick();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [intervalMs, enabled]);
}
